/**
 * Use Case Selection View
 *
 * Step 1 of the genogram two-step wizard: pick a use case. Appears when
 * starting a new genogram (no nodes, no selected use case).
 *
 * Styled with the shared start-picker language (components/start-picker) —
 * uniform with the flowchart/org Start pickers: ink gradient shell, coral
 * header badge, white ink-200 cards with the coral hover. The use-case
 * cards keep their richer row layout (description + audience chips); the
 * per-use-case icon gradient stays as the identity mark.
 */

import React from 'react';
import { HeartPulse } from 'lucide-react';
import { getUseCases } from '../../domains/genogram/config/useCases';
import type { UseCaseType } from '../../domains/genogram/models/UseCase';
import { getCommandManager } from '../../hooks/useCommandManager';
import { SetUseCaseCommand } from '../../domains/genogram/commands/SetUseCaseCommand';
import { useTranslation } from '@/hooks/useTranslation';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { ACTIVATION_FUNNEL } from '../../domains/analytics/constants/MetricEvents';
import {
  StartPickerShell,
  StartPickerCard,
  StartPickerBlankCard,
} from '../start-picker/StartPicker';

export interface UseCaseSelectionViewProps {
  onStartBlank: () => void
}

export const UseCaseSelectionView: React.FC<UseCaseSelectionViewProps> = ({ onStartBlank }) => {
  const commandManager = getCommandManager();
  const { t, i18n } = useTranslation('genogram');
  const useCases = React.useMemo(() => getUseCases(i18n.language), [i18n.language]);
  // Tile order matches the other pickers: content starters first, Blank last.
  const starterCases = useCases.filter((u) => u.id !== 'blank');
  const blankCase = useCases.find((u) => u.id === 'blank');

  // Impression — the funnel denominator (picker shown → use_case_selected →
  // first_shape). Fires once per mount so PostHog can measure which use cases
  // convert vs. which get skipped/abandoned.
  React.useEffect(() => {
    Tracker.track(ACTIVATION_FUNNEL.USE_CASE_PICKER_SHOWN, {
      domain: 'genogram',
      optionCount: useCases.length,
    });
  }, [useCases.length]);

  const handleSelectUseCase = async (useCaseId: UseCaseType) => {
    // Track use case selection (activation funnel feeder)
    Tracker.track(ACTIVATION_FUNNEL.USE_CASE_SELECTED, { useCase: useCaseId });

    // ARCHITECTURE COMPLIANCE: Use commands for all state mutations
    const command = new SetUseCaseCommand(useCaseId);
    await commandManager.execute(command);

    if (useCaseId === 'blank') {
      onStartBlank();
    }
  };

  return (
    <StartPickerShell
      icon={<HeartPulse className="size-5" />}
      title={t('useCaseSelection.title')}
      subtitle={t('useCaseSelection.subtitle')}
      testId="tid.onboarding.useCaseSelection"
      maxWidthClass="max-w-3xl"
      footer={t('useCaseSelection.footerNote')}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {/* No audience chips — the titles already carry the audience
            ("Family Therapy & Counseling"); icon + title + one line keeps
            the cards scannable at the flowchart-tile density. */}
        {starterCases.map((useCase) => (
          <StartPickerCard
            key={useCase.id}
            icon={useCase.icon}
            name={useCase.name}
            description={useCase.description}
            onClick={() => handleSelectUseCase(useCase.id)}
            testId={`tid.onboarding.useCase.card.${useCase.id}`}
            dataEvent={{ click: 'onboarding.step1', params: { value: useCase.id } }}
          />
        ))}
        {blankCase && (
          <StartPickerBlankCard
            name={blankCase.name}
            description={blankCase.description}
            onClick={() => handleSelectUseCase(blankCase.id)}
            testId={`tid.onboarding.useCase.card.${blankCase.id}`}
            dataEvent={{ click: 'onboarding.step1', params: { value: blankCase.id } }}
          />
        )}
      </div>
    </StartPickerShell>
  );
};
